import type { CityConfig } from "../city-template/types";

export const claremontConfig: CityConfig = {
  city: "Claremont",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "claremont",
  route: "/cities/claremont",
  title: "Emergency Passport Claremont, NH | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Claremont, NH — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Sullivan County and western New Hampshire travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Claremont, NH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Claremont and western New Hampshire travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Claremont%20New%20Hampshire%20historic%20downtown%20main%20street%20with%20charming%20Victorian%20brick%20buildings%20and%20the%20Opera%20House%20tower%2C%20the%20Connecticut%20River%20valley%20in%20the%20background%2C%20warm%20autumn%20afternoon%20light%20with%20golden%20and%20orange%20foliage%2C%20clean%20and%20inviting%20small%20town%20scene%2C%20no%20visible%20text%20or%20people%2C%20professional%20architectural%20photography%2C%20warm%20earth%20tones%20and%20soft%20sky%2C%20photorealistic%20cityscape%2C%20New%20England%20small-town%20charm&width=1800&height=700&seq=claremont-nh-hero-001&orientation=landscape",
  heroImageAlt: "Claremont New Hampshire historic downtown with Victorian brick buildings and the Connecticut River valley",
  geoPosition: "43.3767;-72.3467",
  geoPlacename: "Claremont, New Hampshire",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Customs House St, Boston, MA 02103",
    distance: "Located in downtown Boston — approximately 2.5 to 3 hours from Claremont via I-91 and I-93",
  },
  nearbyCitiesZips: [
    { name: "Claremont", zips: "03743" },
    { name: "Newport", zips: "03773" },
    { name: "Charlestown", zips: "03603" },
    { name: "Cornish", zips: "03745" },
    { name: "Plainfield", zips: "03781" },
    { name: "Unity", zips: "03743" },
    { name: "Springfield", zips: "05150–05156" },
    { name: "Croydon", zips: "03773" },
    { name: "Lempster", zips: "03605" },
    { name: "Acworth", zips: "03601" },
    { name: "Alstead", zips: "03602" },
    { name: "Langdon", zips: "03602" },
    { name: "Walpole", zips: "03608" },
    { name: "Westmoreland", zips: "03467" },
    { name: "Goshen", zips: "03752" },
    { name: "Washington", zips: "03280" },
    { name: "Bellows Falls", zips: "05101" },
    { name: "Hartland", zips: "05048" },
    { name: "Windsor", zips: "05089" },
    { name: "Woodstock", zips: "05091" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent western New Hampshire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Manchester", route: "/cities/manchester" },
    { name: "Nashua", route: "/cities/nashua" },
    { name: "Portsmouth", route: "/cities/portsmouth" },
    { name: "Keene", route: "/cities/keene" },
    { name: "Dover", route: "/cities/dover" },
    { name: "Rochester", route: "/cities/rochester" },
    { name: "Lebanon", route: "/cities/lebanon" },
    { name: "Hanover", route: "/cities/hanover" },
    { name: "Newport", route: "/cities/newport" },
    { name: "Springfield VT", route: "/cities/springfield-vt" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Claremont remotely via secure courier. We do not have a walk-in office in Claremont — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for western New Hampshire travelers.",
  fpcOfficeDistance: "Serving all of Sullivan County and western New Hampshire",
  serviceAreaIntro: "From Claremont's historic Opera House district to the Sugar River neighborhoods, from the West Claremont area to the Connecticut River Valley — Fast Passport Center serves the entire Claremont community. We regularly help travelers from Newport, Charlestown, Cornish, and Plainfield get their passports fast.",
  nearbyCitiesIntro: "We serve all Claremont neighborhoods and nearby western New Hampshire cities including Newport, Charlestown, Cornish, Plainfield, Unity, Croydon, Lempster, Acworth, Alstead, Westmoreland, and Goshen. We also serve Vermont towns across the Connecticut River including Springfield VT, Bellows Falls, Hartland, Windsor, and Woodstock. If you are in the greater Claremont area, we have you covered.",
  howItWorksIntro: "Most Claremont travelers complete everything online and ship documents overnight. No need to drive to Boston or Hartford — our courier handles the in-person delivery for you.",
  mistakesIntro: "With Lebanon Municipal Airport (LEB) and easy access to Hartford (BDL), western New Hampshire travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Claremont and western New Hampshire travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Claremont? Here are the most common ones we hear from western New Hampshire travelers.",
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Claremont international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jennifer M.",
      location: "Claremont, NH",
      rating: 5,
      text: "My study abroad program in Ireland started in 5 days and I realized my passport was expired. Fast Passport Center got it renewed in 48 hours. Saved my semester abroad.",
      service: "Passport Renewal",
    },
    {
      name: "Robert K.",
      location: "Newport, NH",
      rating: 5,
      text: "Needed an emergency passport for a business trip to Montreal. They walked me through the process and had it ready in 2 days. Very professional service.",
      service: "Emergency Passport",
    },
    {
      name: "Susan L.",
      location: "Charlestown, NH",
      rating: 5,
      text: "Lost my passport before a family vacation to Aruba. The team was patient, explained everything, and got my replacement in 3 days. Highly recommend.",
      service: "Lost Passport",
    },
    {
      name: "David P.",
      location: "Cornish, NH",
      rating: 5,
      text: "As someone who lives in rural western NH, I thought I'd have to drive to Boston. Fast Passport Center made it so easy — shipped everything overnight and got my passport in 4 days.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "claremont-nh-1",
      name: "Claremont Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "140 Broad St",
      city: "Claremont",
      state: "NH",
      zip: "03743",
      phone: "(603) 542-5261",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Claremont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Claremont. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "claremont-nh-2",
      name: "Newport Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "12 N Main St",
      city: "Newport",
      state: "NH",
      zip: "03773",
      phone: "(603) 863-5510",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.2 miles from downtown Newport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Sullivan County residents. Limited hours — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "claremont-nh-3",
      name: "Charlestown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "26 Main St",
      city: "Charlestown",
      state: "NH",
      zip: "03603",
      phone: "(603) 826-7716",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.3 miles from downtown Charlestown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves westernmost Sullivan County near the Vermont border. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "claremont-nh-4",
      name: "Walpole Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "22 Main St",
      city: "Walpole",
      state: "NH",
      zip: "03608",
      phone: "(603) 756-4376",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.2 miles from downtown Walpole",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Sullivan County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "claremont-nh-5",
      name: "Claremont City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "58 Opera House Sq",
      city: "Claremont",
      state: "NH",
      zip: "03743",
      phone: "(603) 542-7003",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from City Hall",
      website: "https://www.claremontnh.com/",
      websiteLabel: "Claremont City Clerk",
      notes: "Major municipal facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "claremont-nh-6",
      name: "Alstead Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15 Main St",
      city: "Alstead",
      state: "NH",
      zip: "03602",
      phone: "(603) 835-6331",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.3 miles from downtown Alstead",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southern Sullivan County near the Keene area. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Claremont photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Sullivan County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Claremont applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Claremont families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Claremont travelers often assume standard processing will be fine. It takes 6–13 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Claremont." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Claremont address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Claremont before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Claremont for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most western New Hampshire travelers." },
    { q: "How fast can I renew my passport in Claremont?", a: "Fast Passport Center offers fast passport renewal in Claremont in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Claremont in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Claremont?", a: "Same-day passport appointments are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Claremont residents." },
    { q: "Can I expedite a child passport in Claremont?", a: "Yes. Fast Passport Center expedites child passports in Claremont for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Claremont with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Claremont?", a: "With Fast Passport Center's emergency courier service, Claremont travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Boston Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Claremont?", a: "Popular options include the Claremont Post Office (03743), Newport Post Office (03773), Charlestown Post Office (03603), Walpole Post Office (03608), and Claremont City Clerk (03743). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Claremont?", a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Newport or Charlestown?", a: "We serve all of western New Hampshire including Newport, Charlestown, Cornish, Plainfield, Unity, Croydon, Lempster, Acworth, Alstead, Westmoreland, and Goshen. We also serve Vermont towns across the river. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Claremont?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Claremont", address: "370 Washington St, Claremont, NH 03743", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Claremont", address: "227 Sullivan St, Claremont, NH 03743", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Rite Aid — Newport", address: "28 N Main St, Newport, NH 03773", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Claremont", address: "20 Dunning St, Claremont, NH 03743", hours: "Mon–Sun: 6AM–11PM" },
    { name: "UPS Store — Claremont", address: "161 Elm St, Claremont, NH 03743", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Shaw's — Claremont", address: "20 Dunning St, Claremont, NH 03743", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Hannaford — Newport", address: "11 John Stark Hwy, Newport, NH 03773", hours: "Mon–Sun: 7AM–10PM" },
    { name: "Dollar General — Charlestown", address: "11 Main St, Charlestown, NH 03603", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    { name: "DMV — Newport", address: "10 Main St, Newport, NH 03773", hours: "Mon–Fri: 8AM–5PM", note: "Full-service center for western New Hampshire residents. Appointment recommended.", featured: true },
    { name: "DMV — Keene", address: "9 West St, Keene, NH 03431", hours: "Mon–Fri: 8AM–5PM", note: "Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "DMV — Claremont", address: "140 Broad St, Claremont, NH 03743", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID processing. Walk-ins limited — book online first.", featured: false },
    { name: "DMV — Lebanon", address: "12 Mosco Dr, Lebanon, NH 03766", hours: "Mon–Fri: 8AM–5PM", note: "Full-service DMV for Upper Valley residents.", featured: true },
  ],
  airports: [
    { code: "LEB", name: "Lebanon Municipal Airport", distance: "25 miles from Claremont", address: "5 Airpark Rd, West Lebanon, NH 03784", note: "Regional airport with commercial flights to Boston and New York. Closest commercial airport to Claremont.", flightUrl: "https://www.lebanonairport.com/", flightLabel: "Lebanon Airport", featured: true },
    { code: "MHT", name: "Manchester-Boston Regional Airport", distance: "75 miles from Claremont", address: "1 Airport Rd, Manchester, NH 03103", note: "Major commercial airport for Claremont travelers. International flights to Canada and Europe.", flightUrl: "https://www.flymanchester.com/", flightLabel: "MHT Flight Info", featured: false },
    { code: "BDL", name: "Bradley International Airport", distance: "95 miles from Claremont", address: "1 Schoephoester Rd, Windsor Locks, CT 06096", note: "Major international hub for long-haul flights to Europe and the Caribbean. Accessible via I-91.", flightUrl: "https://www.bradleyairport.com/", flightLabel: "Bradley Airport", featured: false },
  ],
};